import React, { useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import MessageItem from './MessageItem';
import './MessageList.css';

const MessageList = ({ messages, loading, hasMore, onLoadMore, messagesEndRef, messagesContainerRef, onScroll }) => {
  const { user } = useAuth();
  const listRef = useRef(null);
  const loadingRef = useRef(null);
  
  // Use provided container ref or create one
  const containerRef = messagesContainerRef || listRef;

  useEffect(() => {
    // Scroll to bottom when new messages arrive
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length, messagesEndRef]);

  useEffect(() => {
    // Intersection Observer for infinite scroll
    if (!loadingRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          onLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(loadingRef.current);

    return () => {
      if (loadingRef.current) {
        observer.unobserve(loadingRef.current);
      }
    };
  }, [hasMore, loading, onLoadMore]);

  // Show loading indicator when initially loading (no messages yet)
  const isInitialLoading = loading && messages.length === 0;

  return (
    <div className="message-list" ref={containerRef} onScroll={onScroll}>
      {isInitialLoading ? (
        <div className="message-list-initial-loading">
          <div className="spinner"></div>
          <span>Loading messages...</span>
        </div>
      ) : (
        <>
          {hasMore && (
            <div ref={loadingRef} className="message-list-loading">
              {loading && <div className="spinner-small"></div>}
            </div>
          )}

          {messages.length === 0 && !loading ? (
            <div className="message-list-empty">
              <p>No messages yet. Start the conversation!</p>
            </div>
          ) : (
            messages.map((message) => (
              <div key={message.id} data-message-id={message.id}>
                <MessageItem
                  message={message}
                  isOwn={message.user_id === user?.id}
                />
              </div>
            ))
          )}

          <div ref={messagesEndRef} />
        </>
      )}
    </div>
  );
};

export default MessageList;

